import React, { useState, useEffect, useCallback } from 'react';
import { billingAPI, patientAPI, labTestAPI, imagingTestAPI } from '../../lib/api';
import { formatDate, formatDateTime, formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { FileText, Download, Receipt, FlaskConical, Scan, User } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ReportsPage = () => {
  const [reportType, setReportType] = useState('bill');
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patients, setPatients] = useState([]);
  const [bills, setBills] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [imagingTests, setImagingTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await patientAPI.getAll({});
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      if (reportType === 'bill') {
        const response = await billingAPI.getAll({ patient_id: selectedPatient });
        setBills(response.data);
      } else if (reportType === 'lab') {
        const response = await labTestAPI.getAll({ patient_id: selectedPatient });
        setLabTests(response.data);
      } else if (reportType === 'imaging') {
        const response = await imagingTestAPI.getAll({ patient_id: selectedPatient });
        setImagingTests(response.data);
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [selectedPatient, reportType]);


  useEffect(() => {
    if (selectedPatient) {
      fetchReportData();
    }
  }, [selectedPatient, reportType, fetchReportData]);
  const getSelectedPatient = () => patients.find(p => p.id === selectedPatient);

  const generateBillPDF = (bill) => {
    const doc = new jsPDF();
    const patient = getSelectedPatient();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(229, 57, 53); // Healventure Red
    doc.text('Healventure Medical Centre', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text('HMC Buddy - Hospital Management System', 105, 28, { align: 'center' });
    
    // Bill Title
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.text('INVOICE', 105, 45, { align: 'center' });
    
    // Bill Details
    doc.setFontSize(10);
    doc.text(`Bill ID: ${bill.bill_id}`, 15, 60);
    doc.text(`Date: ${formatDateTime(bill.created_at)}`, 15, 67);
    doc.text(`Status: ${bill.status.toUpperCase()}`, 15, 74);
    
    // Patient Details
    doc.text(`Patient: ${patient?.full_name}`, 120, 60);
    doc.text(`Patient ID: ${patient?.patient_id}`, 120, 67);
    doc.text(`Phone: ${patient?.phone}`, 120, 74);
    
    // Line
    doc.setDrawColor(229, 57, 53);
    doc.line(15, 82, 195, 82);
    
    // Items Table
    const tableData = bill.items.map(item => [
      item.description,
      item.category,
      item.quantity,
      formatCurrency(item.unit_price),
      formatCurrency(item.total)
    ]);
    
    doc.autoTable({
      startY: 88,
      head: [['Description', 'Category', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [229, 57, 53] },
      styles: { fontSize: 9 },
    });
    
    // Totals
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Subtotal: ${formatCurrency(bill.subtotal)}`, 140, finalY);
    if (bill.discount_amount > 0) {
      doc.text(`Discount (${bill.discount_percent}%): -${formatCurrency(bill.discount_amount)}`, 140, finalY + 7);
    }
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`Total: ${formatCurrency(bill.total_amount)}`, 140, finalY + 17);
    doc.setFont(undefined, 'normal');
    doc.text(`Paid: ${formatCurrency(bill.paid_amount || 0)}`, 140, finalY + 24);
    doc.setTextColor(229, 57, 53);
    doc.text(`Balance: ${formatCurrency(bill.total_amount - (bill.paid_amount || 0))}`, 140, finalY + 31);
    
    // Footer
    doc.setTextColor(100);
    doc.setFontSize(8);
    doc.text('Thank you for choosing Healventure Medical Centre', 105, 280, { align: 'center' });
    
    doc.save(`Invoice_${bill.bill_id}.pdf`);
    toast.success('Bill PDF generated');
  };

  const generateLabReportPDF = (test) => {
    const doc = new jsPDF();
    const patient = getSelectedPatient();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(229, 57, 53);
    doc.text('Healventure Medical Centre', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text('Laboratory Report', 105, 28, { align: 'center' });
    
    // Test Details
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(`Test ID: ${test.test_id}`, 15, 50);
    doc.text(`Test Name: ${test.test_name}`, 15, 57);
    doc.text(`Category: ${test.test_category}`, 15, 64);
    doc.text(`Order Date: ${formatDateTime(test.created_at)}`, 15, 71);
    doc.text(`Status: ${test.status.toUpperCase()}`, 15, 78);
    
    // Patient Details
    doc.text(`Patient: ${patient?.full_name}`, 120, 50);
    doc.text(`Patient ID: ${patient?.patient_id}`, 120, 57);
    doc.text(`Age/Gender: ${patient?.gender}`, 120, 64);
    doc.text(`Phone: ${patient?.phone}`, 120, 71);
    
    // Line
    doc.setDrawColor(229, 57, 53);
    doc.line(15, 88, 195, 88);
    
    // Results
    doc.setFontSize(14);
    doc.setTextColor(229, 57, 53);
    doc.text('Test Results', 15, 100);
    
    doc.setFontSize(10);
    doc.setTextColor(0);
    if (test.result) {
      const splitResult = doc.splitTextToSize(test.result, 170);
      doc.text(splitResult, 15, 112);
    } else {
      doc.text('Results pending...', 15, 112);
    }
    
    if (test.result_date) {
      doc.text(`Result Date: ${formatDateTime(test.result_date)}`, 15, 160);
    }
    
    // Footer
    doc.setTextColor(100);
    doc.setFontSize(8);
    doc.text('This report is generated electronically and is valid without signature.', 105, 280, { align: 'center' });
    
    doc.save(`LabReport_${test.test_id}.pdf`);
    toast.success('Lab report PDF generated');
  };

  const generateImagingReportPDF = (test) => {
    const doc = new jsPDF();
    const patient = getSelectedPatient();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(229, 57, 53);
    doc.text('Healventure Medical Centre', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text('Radiology Report', 105, 28, { align: 'center' });
    
    // Test Details
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(`Study ID: ${test.test_id}`, 15, 50);
    doc.text(`Study Type: ${test.test_type}`, 15, 57);
    doc.text(`Body Part: ${test.body_part}`, 15, 64);
    doc.text(`Study Date: ${formatDateTime(test.created_at)}`, 15, 71);
    doc.text(`Status: ${test.status.toUpperCase()}`, 15, 78);
    
    // Patient Details
    doc.text(`Patient: ${patient?.full_name}`, 120, 50);
    doc.text(`Patient ID: ${patient?.patient_id}`, 120, 57);
    doc.text(`Phone: ${patient?.phone}`, 120, 64);
    
    // Line
    doc.setDrawColor(229, 57, 53);
    doc.line(15, 88, 195, 88);
    
    // Clinical Notes
    if (test.clinical_notes) {
      doc.setFontSize(12);
      doc.setTextColor(229, 57, 53);
      doc.text('Clinical Indication', 15, 100);
      doc.setFontSize(10);
      doc.setTextColor(0);
      const splitNotes = doc.splitTextToSize(test.clinical_notes, 170);
      doc.text(splitNotes, 15, 110);
    }
    
    // Findings
    doc.setFontSize(14);
    doc.setTextColor(229, 57, 53);
    doc.text('Findings & Impression', 15, 135);
    
    doc.setFontSize(10);
    doc.setTextColor(0);
    if (test.report) {
      const splitReport = doc.splitTextToSize(test.report, 170);
      doc.text(splitReport, 15, 147);
    } else {
      doc.text('Report pending...', 15, 147);
    }
    
    if (test.report_date) {
      doc.text(`Report Date: ${formatDateTime(test.report_date)}`, 15, 200);
    }
    
    // Footer
    doc.setTextColor(100);
    doc.setFontSize(8);
    doc.text('This report is generated electronically. Please correlate clinically.', 105, 280, { align: 'center' });
    
    doc.save(`ImagingReport_${test.test_id}.pdf`);
    toast.success('Imaging report PDF generated');
  };

  return (
    <div data-testid="reports-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Reports</h1>
            <p className="text-slate-500">Generate and download patient reports</p>
          </div>
        </div>
      </header>

      <div className="page-content">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-500" />
                Generate Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Report Type</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger data-testid="select-report-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bill">
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4" />
                        <span>Billing Invoice</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="lab">
                      <div className="flex items-center gap-2">
                        <FlaskConical className="w-4 h-4" />
                        <span>Lab Test Report</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="imaging">
                      <div className="flex items-center gap-2">
                        <Scan className="w-4 h-4" />
                        <span>Imaging Report</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Patient</Label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger data-testid="select-patient">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{p.full_name} ({p.patient_id})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedPatient && (
                <div className="p-4 bg-slate-50 rounded-lg mt-4">
                  <p className="text-sm text-slate-500 mb-1">Selected Patient</p>
                  <p className="font-medium">{getSelectedPatient()?.full_name}</p>
                  <p className="text-sm text-slate-500">{getSelectedPatient()?.patient_id}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg font-semibold font-[Manrope]">
                  {reportType === 'bill' && 'Billing Records'}
                  {reportType === 'lab' && 'Lab Test Records'}
                  {reportType === 'imaging' && 'Imaging Test Records'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {!selectedPatient ? (
                  <div className="p-8 text-center text-slate-500">
                    Select a patient to view available reports
                  </div>
                ) : loading ? (
                  <div className="p-8 text-center text-slate-500">Loading...</div>
                ) : (
                  <>
                    {/* Bills Table */}
                    {reportType === 'bill' && (
                      bills.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No billing records found.</div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Bill ID</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Total</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Download</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {bills.map((bill) => (
                              <TableRow key={bill.id}>
                                <TableCell className="font-mono">{bill.bill_id}</TableCell>
                                <TableCell>{formatDate(bill.created_at)}</TableCell>
                                <TableCell className="font-semibold">{formatCurrency(bill.total_amount)}</TableCell>
                                <TableCell>
                                  <Badge className={bill.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                    {bill.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateBillPDF(bill)}
                                    data-testid={`download-bill-${bill.id}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    PDF
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )
                    )}

                    {/* Lab Tests Table */}
                    {reportType === 'lab' && (
                      labTests.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No lab test records found.</div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Test ID</TableHead>
                              <TableHead>Test Name</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Download</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {labTests.map((test) => (
                              <TableRow key={test.id}>
                                <TableCell className="font-mono">{test.test_id}</TableCell>
                                <TableCell>{test.test_name}</TableCell>
                                <TableCell>{formatDate(test.created_at)}</TableCell>
                                <TableCell>
                                  <Badge className={test.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                    {test.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateLabReportPDF(test)}
                                    data-testid={`download-lab-${test.id}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    PDF
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )
                    )}

                    {/* Imaging Tests Table */}
                    {reportType === 'imaging' && (
                      imagingTests.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">No imaging test records found.</div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Study ID</TableHead>
                              <TableHead>Study Type</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Download</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {imagingTests.map((test) => (
                              <TableRow key={test.id}>
                                <TableCell className="font-mono">{test.test_id}</TableCell>
                                <TableCell>{test.test_type} - {test.body_part}</TableCell>
                                <TableCell>{formatDate(test.created_at)}</TableCell>
                                <TableCell>
                                  <Badge className={test.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                    {test.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateImagingReportPDF(test)}
                                    data-testid={`download-imaging-${test.id}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    PDF
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
